//
// Copyright (c) 2020 Astute Global, Inc.
//
// A module for the Site Map editor.
//

define([
  "marionette",
  "underscore",
  "messaging/messenger",
  "./siteMapView",
  "modules/upload/contentUploadModel",
  "./siteMapUploads/siteMapUploadsModule",
], function (Marionette, _, Messenger, SiteMapView, ContentUploadModel, SiteMapUploadsModule) {
  "use strict";

  var SiteMapController = Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "siteMap";
      this.region = options.region;
      this.siteMap = null;
      this._setUpVents();
    },
    show: function (siteMap) {
      this.view = new SiteMapView.siteMapView({ model: siteMap });
      this.uploads = this.addSubModule(
        new SiteMapUploadsModule({
          region: this.view.uploads,
        }),
      );
      this.siteMap = siteMap;
      siteMap.languages = Messenger.reqres.request("kb:languages");
      siteMap.touchPoints = Messenger.reqres.request("kb:touchpoints");
      siteMap.privilegedAreaRoles = Messenger.reqres.request("kb:privileged:area:roles");
      this.siteMap.on("invalid", this._onSiteMapInvalid.bind(this));
      this.view.on("add:privilegedArea", this._onAddPrivilegedArea.bind(this));
      this.view.on("add:touchPoint", this._onAddTouchPoint.bind(this));
      this.view.on("change:languageId", this._onChangeLanguageId.bind(this));
      this.view.on("change:primaryTouchPointId", this._onChangePrimaryTouchPointId.bind(this));
      this.view.on("remove:css", this._onRemoveCss.bind(this));
      this.view.on("remove:siteMap", this._onRemoveSiteMap.bind(this));
      this.view.on("remove:privilegedArea", this._onRemovePrivilegedArea.bind(this));
      this.view.on("remove:touchPoint", this._onRemoveTouchPoint.bind(this));
      this.view.on("show", this._onShow.bind(this));
      this.view.on("upload:css", this._onChangeCss.bind(this));
      this.view.on("upload:template", this._onChangeTemplate.bind(this));
      this.showRegion(this.view);
    },
    _handleUpload: function (file, form, type) {
      if (!file || !form) {
        return;
      }
      // NOTE: The input for the template already includes accept="text/html" so the browser should already
      // limit uploads to HTML files.
      var fileName = file.name;
      var fileSize = file.size;
      if (fileSize > ContentUploadModel.contentUpload.MAX_SIZE) {
        Messenger.okDialog("content.dialog.tooLarge");
        console.log("File size exceeds maximum allowed");
        return;
      }
      var touchPointIds = this.siteMap.get("touchPointIds");
      if (!touchPointIds || !touchPointIds.length) {
        Messenger.okDialog("admin.siteMaps.touchPointsEmptyError");
        console.log("No TouchPoints were added to the Site Map");
        return;
      }
      //upload the file to the upload server
      var uploadModel = new ContentUploadModel.contentUpload({
        kbId: this.siteMap.get("kbId"),
        languageId: this.siteMap.get("languageId"),
        touchPointId: touchPointIds[0],
      });
      Messenger.vent.once(
        "content:upload:changed",
        function (isSuccess, fileName, fileSize, isUploaded, uploadId) {
          if (isSuccess) {
            if (type == "css") {
              this.siteMap.set("cssUploadId", uploadId);
              this.siteMap.set("cssFileName", fileName);
            } else {
              this.siteMap.set("templateUploadId", uploadId);
              this.siteMap.set("templateFileName", fileName);
            }
          }
        },
        this,
      );
      Messenger.reqres.request("upload:content", form, fileName, fileSize, uploadModel);
    },
    _onAddPrivilegedArea: function (privilegedAreaRoleId) {
      var privilegedAreaRoleIds = this.siteMap.get("privilegedAreaRoleIds");
      if (!privilegedAreaRoleIds) {
        privilegedAreaRoleIds = [privilegedAreaRoleId];
      } else {
        privilegedAreaRoleIds = privilegedAreaRoleIds.concat([privilegedAreaRoleId]);
      }
      this.siteMap.set("privilegedAreaRoleIds", privilegedAreaRoleIds);
    },
    _onAddTouchPoint: function (touchPointId) {
      var touchPointIds = this.siteMap.get("touchPointIds");
      if (!touchPointIds) {
        touchPointIds = [touchPointId];
      } else {
        touchPointIds = touchPointIds.concat([touchPointId]);
      }
      this.siteMap.set("touchPointIds", touchPointIds);
    },
    _onChangeCss: function (file, form) {
      this._handleUpload(file, form, "css");
    },
    _onChangeLanguageId: function (languageId) {
      this.siteMap.set("languageId", languageId);
    },
    _onChangePrimaryTouchPointId: function (touchPointId) {
      var touchPointIds = this.siteMap.get("touchPointIds");
      // If there are no TouchPoints yet, or only a single TouchPoint, then allow the
      // select to behave like a normal select
      if (!touchPointIds || touchPointIds.length < 2) {
        this.siteMap.set("touchPointIds", [touchPointId]);
        return;
      }
      // If there are multiple TouchPoints, then allow the full list to be maintained in
      // a way that will keep all TouchPoints intact.
      var existingIndex = _.indexOf(touchPointIds, touchPointId);
      if (existingIndex === 0) {
        // Nothing to do, it is already the primary
        return;
      }
      if (existingIndex > 0) {
        touchPointIds = [touchPointId].concat(
          _.filter(touchPointIds, function (id) {
            return id !== touchPointId;
          }),
        );
      } else {
        touchPointIds = [touchPointId].concat(touchPointIds);
      }
      this.siteMap.set("touchPointIds", touchPointIds);
    },
    _onChangeTemplate: function (file, form) {
      this._handleUpload(file, form, "template");
    },
    _onDeleteSiteMap: function () {
      this.region.reset();
      this.siteMap = null;
    },
    _onRemoveCss: function () {
      this.siteMap.set("cssUploadId", "");
      this.siteMap.set("cssFileName", "");
    },
    _onRemovePrivilegedArea: function (privilegedAreaRoleId) {
      var privilegedAreaRoleIds = _.filter(
        this.siteMap.get("privilegedAreaRoleIds"),
        function (parId) {
          return parId != privilegedAreaRoleId;
        },
      );
      this.siteMap.set("privilegedAreaRoleIds", privilegedAreaRoleIds);
    },
    _onRemoveSiteMap: function () {
      Messenger.vent.trigger("delete:siteMap", this.siteMap);
    },
    _onRemoveTouchPoint: function (touchPointId) {
      var touchPointIds = _.filter(this.siteMap.get("touchPointIds"), function (tpId) {
        return tpId != touchPointId;
      });
      this.siteMap.set("touchPointIds", touchPointIds);
    },
    _onShow: function () {
      this.uploads.show(this.siteMap, this.siteMap.uploads());
    },
    _onSiteMapInvalid: function (model, error) {
      if (error.templateUploadId) {
        Messenger.vent.trigger("cancel:textbox");
        this.view.templateError(error.templateUploadId);
      }
      if (error.touchPointIds) {
        Messenger.vent.trigger("cancel:textbox");
        this.view.touchPointsError(error.touchPointIds);
      }
    },
    _setUpVents: function () {
      Messenger.vent.on("siteMap:selected", this.show, this);
      Messenger.vent.on("delete:siteMap", this._onDeleteSiteMap, this);
    },
  });

  return SiteMapController;
});
